/**
 * Hook: useOrganizationMembers
 * Responsabilidade: Gerenciar lista de membros da organização e ações sobre eles
 */

import { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import {
    getOrganizationMembers,
    updateMemberRole as updateMemberRoleService,
    removeMember as removeMemberService,
    sendPasswordReset as sendPasswordResetService,
    OrganizationMember
} from '../../../services/modules/organizationService';
import { notify } from '../../../utils/toast';

export const useOrganizationMembers = () => {
    const { currentOrganization } = useOrganization();
    const [members, setMembers] = useState<OrganizationMember[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadMembers = useCallback(async () => {
        if (!currentOrganization) {
            setIsLoading(false);
            setMembers([]);
            return;
        }

        try {
            setIsLoading(true);
            const data = await getOrganizationMembers(currentOrganization.id);
            setMembers(data);
        } catch (err: any) {
            console.error('[useOrganizationMembers] Error:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [currentOrganization]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers]);

    /**
     * Atualiza a role de um membro
     */
    const updateMemberRole = useCallback(async (memberId: string, newRole: 'admin' | 'member' | 'viewer') => {
        try {
            await updateMemberRoleService(memberId, newRole);
            notify.success('Função do membro atualizada com sucesso!');
            await loadMembers();
        } catch (err: any) {
            notify.error(err?.message || 'Erro ao atualizar função do membro');
            throw err;
        }
    }, [loadMembers]);

    /**
     * Remove um membro da organização
     * Desatribui dados antes de remover
     */
    const deleteMember = useCallback(async (memberId: string, userId: string) => {
        try {
            await removeMemberService(memberId, userId);
            notify.success('Membro removido com sucesso!');
            await loadMembers();
        } catch (err: any) {
            notify.error('Erro ao remover membro');
            throw err;
        }
    }, [loadMembers]);

    /**
     * Envia email de redefinição de senha para o membro
     */
    const sendPasswordReset = useCallback(async (email: string) => {
        try {
            await sendPasswordResetService(email);
            notify.success('Email de redefinição de senha enviado!');
        } catch (err: any) {
            notify.error('Erro ao enviar email de redefinição');
            throw err;
        }
    }, []);

    return {
        members,
        isLoading,
        error,
        refreshMembers: loadMembers,
        updateMemberRole,
        deleteMember,
        sendPasswordReset
    };
};
